import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, AiAgent, AiAgentsResponse } from '../services/api';
import { AiAgentSettingsModal } from './AiAgentSettingsModal';
import { AiBotIcon } from './icons/AppIcon';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { usePermissions } from '../hooks/usePermissions';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';

/**
 * Team → AI agents: the AI agents the subscription includes (their own number, not counted in
 * the user seats). Each is a team member that never signs in; what it does is recorded under it.
 * Shows which are active or suspended and why; AI admins rename, pause, choose the default
 * agent, and — when the plan allows fewer agents than exist — which ones stay active.
 * `onOpenAgent` opens an agent's settings (persona, coverage, handoff).
 */

const errorOf = (res: any, fallback: string) => res?.errors?.[0]?.message || res?.errors?.[0] || res?.message || fallback;

export function AiAgentsSection({ onOpenAgent }: { onOpenAgent?: (agent: AiAgent) => void }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { showNotification } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canEdit = hasPermissionAtLeast('ai.manageConfig', 'edit');
  const [data, setData] = useState<AiAgentsResponse | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [settingsFor, setSettingsFor] = useState<AiAgent | null>(null);

  const load = useCallback(() => {
    apiService.getAiAgents().then((res) => { if (res.statusCode === 1000 && res.data) setData(res.data); }).catch(() => undefined);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key: string, action: () => Promise<any>) => {
    setBusy(key);
    try {
      const res = await action();
      if (res.statusCode !== 1000) throw new Error(errorOf(res, t('aiAgents.actionFailed')));
      if (res.data?.agents) setData(res.data);
      else load();
    } catch (error: any) {
      showNotification('error', t('common.error'), error?.message || t('aiAgents.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return null;
  // Nothing to show when the plan has no AI agents and none were ever created.
  if (!data.agents.length && !data.unlimited) return null;

  const active = data.agents.filter((a) => a.status === 'active').length;
  const reasonText = (agent: AiAgent) => agent.suspendedReason === 'plan_limit' ? t('aiAgents.suspended.planLimit')
    : agent.suspendedReason === 'no_ai' ? t('aiAgents.suspended.noAi') : t('aiAgents.suspended.noSubscription');

  return (
    <View style={styles.section}>
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.titleRow, { flexDirection: rtl.rowDirection }]}>
          <AiBotIcon size={20} color={colors.primary as string} />
          <Text style={styles.title}>{t('aiAgents.title')}</Text>
        </View>
        <Text style={styles.count}>
          {data.unlimited ? t('aiAgents.countUnlimited', { active }) : t('aiAgents.count', { active, allowed: data.allowed ?? 0 })}
        </Text>
      </View>
      <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('aiAgents.hint')}</Text>
      {!!data.reason && (
        <View style={[styles.banner, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="alert-circle-outline" size={16} color={colors.warning as string} />
          <Text style={[styles.bannerText, { textAlign: rtl.textStart }]}>{data.reason === 'no_ai' ? t('aiAgents.banner.noAi') : t('aiAgents.banner.noSubscription')}</Text>
        </View>
      )}

      {data.agents.map((agent) => {
        const suspended = agent.status === 'suspended';
        const isEditing = editing === agent.id;
        return (
          <View key={agent.id} style={[styles.card, suspended && styles.cardMuted]}>
            <View style={[styles.cardTop, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.avatar, suspended && { backgroundColor: withAlpha(colors.text.secondary as string, 0.15) }]}>
                <AiBotIcon size={18} color={suspended ? (colors.text.secondary as string) : (colors.primary as string)} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <View style={[styles.editRow, { flexDirection: rtl.rowDirection }]}>
                    <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={name} onChangeText={setName} maxLength={60} autoFocus
                      onSubmitEditing={() => name.trim() && run(agent.id, () => apiService.updateAiAgent(agent.id, { name: name.trim() })).then(() => setEditing(null))} />
                    <TouchableOpacity disabled={!name.trim() || busy === agent.id} onPress={() => run(agent.id, () => apiService.updateAiAgent(agent.id, { name: name.trim() })).then(() => setEditing(null))}>
                      <Ionicons name="checkmark-circle" size={22} color={colors.primary as string} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setEditing(null)}>
                      <Ionicons name="close-circle-outline" size={22} color={colors.text.secondary as string} />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={[styles.nameRow, { flexDirection: rtl.rowDirection }]}>
                    <Text style={styles.name} numberOfLines={1}>{agent.name}</Text>
                    <View style={styles.badge}><Text style={styles.badgeText}>{t('aiAgents.badge')}</Text></View>
                    {agent.isDefault && <View style={[styles.badge, styles.badgeDefault]}><Text style={styles.badgeText}>{t('aiAgents.default')}</Text></View>}
                  </View>
                )}
                <Text style={[styles.status, { textAlign: rtl.textStart }, suspended && { color: colors.warning as string }]} numberOfLines={2}>
                  {suspended ? `${t('aiAgents.status.suspended')} · ${reasonText(agent)}` : agent.paused ? t('aiAgents.status.paused') : t('aiAgents.status.active')}
                  {' · '}{t('aiAgents.noLogin')}
                </Text>
              </View>
              {busy === agent.id && <ActivityIndicator size="small" color={colors.primary as string} />}
            </View>
            {canEdit && !isEditing && (
              <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
                {(
                  <TouchableOpacity style={styles.button} onPress={() => (onOpenAgent ? onOpenAgent(agent) : setSettingsFor(agent))}>
                    <Ionicons name="options-outline" size={14} color={colors.primary as string} />
                    <Text style={styles.buttonText}>{t('aiAgents.settings')}</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={styles.button} onPress={() => { setName(agent.name); setEditing(agent.id); }}>
                  <Ionicons name="pencil-outline" size={14} color={colors.primary as string} />
                  <Text style={styles.buttonText}>{t('aiAgents.rename')}</Text>
                </TouchableOpacity>
                {!suspended && (
                  <TouchableOpacity style={styles.button} disabled={busy === agent.id} onPress={() => run(agent.id, () => apiService.updateAiAgent(agent.id, { paused: !agent.paused }))}>
                    <Ionicons name={agent.paused ? 'play-outline' : 'pause-outline'} size={14} color={colors.primary as string} />
                    <Text style={styles.buttonText}>{agent.paused ? t('aiAgents.resume') : t('aiAgents.pause')}</Text>
                  </TouchableOpacity>
                )}
                {!suspended && !agent.isDefault && (
                  <TouchableOpacity style={styles.button} disabled={busy === agent.id} onPress={() => run(agent.id, () => apiService.updateAiAgent(agent.id, { isDefault: true }))}>
                    <Ionicons name="star-outline" size={14} color={colors.primary as string} />
                    <Text style={styles.buttonText}>{t('aiAgents.makeDefault')}</Text>
                  </TouchableOpacity>
                )}
                {suspended && agent.suspendedReason === 'plan_limit' && (
                  <TouchableOpacity style={[styles.button, styles.primary]} disabled={busy === agent.id} onPress={() => run(agent.id, () => apiService.keepAiAgentActive(agent.id, true))}>
                    <Ionicons name="swap-horizontal-outline" size={14} color="white" />
                    <Text style={[styles.buttonText, { color: 'white' }]}>{t('aiAgents.keepActive')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        );
      })}
      {!!settingsFor && (
        <AiAgentSettingsModal agent={settingsFor} onClose={() => setSettingsFor(null)}
          onSaved={(saved) => setData((d) => (d ? { ...d, agents: d.agents.map((a) => (a.id === saved.id ? saved : a)) } : d))} />
      )}
      {canEdit && data.unlimited && (
        <TouchableOpacity style={[styles.add, { flexDirection: rtl.rowDirection }]} disabled={busy === 'add'} onPress={() => run('add', () => apiService.addAiAgent())}>
          <Ionicons name="add-circle-outline" size={18} color={colors.primary as string} />
          <Text style={styles.buttonText}>{t('aiAgents.add')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  section: { gap: spacing.sm, marginBottom: spacing.lg },
  header: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  titleRow: { alignItems: 'center', gap: spacing.sm },
  title: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  count: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  banner: { alignItems: 'center', gap: spacing.xs, padding: spacing.sm, borderRadius: designTokens.borderRadius.md, backgroundColor: withAlpha(colors.warning as string, 0.1) },
  bannerText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  card: { padding: spacing.sm, gap: spacing.xs, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  cardMuted: { opacity: 0.75 },
  cardTop: { alignItems: 'center', gap: spacing.sm },
  avatar: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: withAlpha(colors.primary as string, 0.12) },
  nameRow: { alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap' },
  name: { flexShrink: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  badge: { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8, backgroundColor: withAlpha(colors.primary as string, 0.12) },
  badgeDefault: { backgroundColor: withAlpha('#16A34A', 0.15) },
  badgeText: { fontSize: 10, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  status: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },
  editRow: { alignItems: 'center', gap: spacing.xs },
  input: { flex: 1, minWidth: 0, height: 34, paddingHorizontal: spacing.sm, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, color: colors.text.primary, backgroundColor: colors.background.base },
  actions: { flexWrap: 'wrap', gap: spacing.xs },
  button: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: 6, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border },
  primary: { backgroundColor: colors.primary, borderColor: colors.primary },
  buttonText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  add: { alignItems: 'center', gap: spacing.xs, alignSelf: 'flex-start', paddingVertical: spacing.xs },
});
